import React, { useState, useRef } from 'react';
import { X, Upload, Download, RotateCcw, Save, FolderOpen, Heart, Star } from 'lucide-react';
import { MARKETPLACE_LOGOS } from '../consts';

const AdvancedFilters = ({
    filters,
    setFilters,
    initialFilters,
    marketplace,
    exportToCSV,
    walmartBadges,
    allSuppliers,
    allCategories,
    allBrands = [],
    onSettingsChange,
    isCompactView,
    setIsCompactView,
    afRender,
    setAFRender,
    hasUnscannedDeals,
    autoPackDetection,
    setAutoPackDetection,
}) => {
    const [excludedBrands, setExcludedBrands] = useState(() => {
        const saved = localStorage.getItem('excluded_brands');
        return saved ? JSON.parse(saved) : [];
    });
    const [bannedSuppliers, setBannedSuppliers] = useState(() => {
        const saved = localStorage.getItem('banned_suppliers');
        return saved ? JSON.parse(saved) : [];
    });
    const [showSaveSlots, setShowSaveSlots] = useState(false);
    const [showLoadSlots, setShowLoadSlots] = useState(false);
    const [showAmazonModal, setShowAmazonModal] = useState(false);
    const [showWalmartModal, setShowWalmartModal] = useState(false);
    const [showEbayModal, setShowEbayModal] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false);
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const [tutorialStep, setTutorialStep] = useState(() =>
        localStorage.getItem('mp_tutorial_seen') !== 'true' ? 0 : null
    );
    const dismissTutorial = () => {
        localStorage.setItem('mp_tutorial_seen', 'true');
        setTutorialStep(null);
        if (marketplaceButtonsRef?.current) {
            marketplaceButtonsRef.current.classList.add('tutorial-highlight');
        }
    };

    const fileInputRef = useRef(null);
    const supplierFileInputRef = useRef(null);
    const generalFiltersRef = useRef(null);
    const marketplaceButtonsRef = useRef(null);

    React.useEffect(() => {
        localStorage.setItem('excluded_brands', JSON.stringify(excludedBrands));
        onSettingsChange();
    }, [excludedBrands]);

    React.useEffect(() => {
        localStorage.setItem('banned_suppliers', JSON.stringify(bannedSuppliers));
        // Update filters to trigger re-filtering
        setFilters(prev => ({
            ...prev,
            suppliers: { ...prev.suppliers, banned_suppliers: bannedSuppliers }
        }));
        onSettingsChange();
    }, [bannedSuppliers]);

    React.useEffect(() => {
        const handler = (e) => {
            document.querySelectorAll('.marketplace-dd').forEach(dd => {
                if (!dd.parentElement.contains(e.target)) dd.classList.add('hidden');
            });
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openWalmartModal = () => {
        setAFRender(p => p + 1);
        setShowWalmartModal(true);
    };

    const openSupplierModal = () => {
        setAFRender(p => p + 1);
        setShowSupplierModal(true);
    };

    const openCategoryModal = () => {
        setAFRender(p => p + 1);
        setShowCategoryModal(true);
    };

    const openBrandModal = () => {
        setAFRender(p => p + 1);
        setShowBrandModal(true);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
    };

    // When modal opens, fetch fresh data
    const openExportModal = () => {
        setAFRender(p => p + 1); // Force refresh
        setShowExportModal(true);
    };

    // Export button in modal calls exportToCSV directly (fresh data)
    const handleExport = () => {
        exportToCSV(); // This now has fresh data from the modal render
        setShowExportModal(false);
    };

    // Save/Load Filter Slots (6 slots)
    const saveFilterToSlot = (slotNumber) => {
        const slotKey = `filter_slot_${slotNumber}`;
        const slotName = prompt(`Name for Filter Slot ${slotNumber}:`, `Filter ${slotNumber}`);
        if (slotName !== null) {
            const filterData = {
                name: slotName || `Filter ${slotNumber}`,
                filters: filters,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem(slotKey, JSON.stringify(filterData));
            setShowSaveSlots(false);
        }
    };

    const loadFilterFromSlot = (slotNumber) => {
        const slotKey = `filter_slot_${slotNumber}`;
        const saved = localStorage.getItem(slotKey);
        if (saved) {
            const filterData = JSON.parse(saved);
            setFilters(filterData.filters);
            setShowLoadSlots(false);
        }
    };

    const getSlotInfo = (slotNumber) => {
        const slotKey = `filter_slot_${slotNumber}`;
        const saved = localStorage.getItem(slotKey);
        if (saved) {
            const filterData = JSON.parse(saved);
            return {
                exists: true,
                name: filterData.name,
                savedAt: new Date(filterData.savedAt).toLocaleString()
            };
        }
        return { exists: false };
    };

    const banSupplier = (supplierName) => {
        setAFRender(p => p + 1);
        if (!supplierName || bannedSuppliers.includes(supplierName)) return;
        setBannedSuppliers(prev => [...prev, supplierName]);

        // Remove from selected suppliers if present
        setFilters(prev => ({
            ...prev,
            suppliers: {
                ...prev.suppliers,
                selected_suppliers: prev.suppliers.selected_suppliers.filter(s => s !== supplierName)
            }
        }));
    };

    const unbanSupplier = (supplierName) => {
        setAFRender(p => p + 1);
        setBannedSuppliers(prev => prev.filter(s => s !== supplierName));
    };

    const toggleSupplierSelection = (supplierName) => {
        setAFRender(p => p + 1);
        setFilters(prev => {
            const isSelected = prev.suppliers.selected_suppliers.includes(supplierName);

            return {
                ...prev,
                suppliers: {
                    ...prev.suppliers,
                    selected_suppliers: isSelected
                        ? prev.suppliers.selected_suppliers.filter(s => s !== supplierName)
                        : [...prev.suppliers.selected_suppliers, supplierName]
                }
            };
        });
    };

    const clearSupplierSelection = () => {
        setAFRender(p => p + 1);
        setFilters(prev => ({
            ...prev,
            suppliers: { ...prev.suppliers, selected_suppliers: [] }
        }));
    };

    const selectAllSuppliers = () => {
        setAFRender(p => p + 1);
        const nonBannedSuppliers = allSuppliers
            .filter(s => !bannedSuppliers.includes(s.name))
            .map(s => s.name);

        setFilters(prev => ({
            ...prev,
            suppliers: { ...prev.suppliers, selected_suppliers: nonBannedSuppliers }
        }));
    };

    const exportBannedSuppliersToCSV = () => {
        const csv = bannedSuppliers.join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'banned_suppliers.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const importBannedSuppliersFromCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            const suppliers = text
                .split(/[\n,]/)
                .map(s => s.trim())
                .filter(Boolean)
                .filter((supplier, index, self) => self.indexOf(supplier) === index);

            setBannedSuppliers(suppliers);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const exportBrandsToCSV = () => {
        const csv = excludedBrands.join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'excluded_brands.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const importBrandsFromCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            const brands = text
                .split(/[\n,]/)
                .map(b => b.trim())
                .filter(Boolean)
                .filter((brand, index, self) => self.indexOf(brand) === index);

            setExcludedBrands(brands);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const addBrand = (brandText) => {
        if (!brandText.trim()) return;

        const newBrands = brandText
            .split(/[\n,]/)
            .map(b => b.trim())
            .filter(Boolean);

        setExcludedBrands(prev => [...new Set([...prev, ...newBrands])]);
    };

    const removeBrand = (brand) => {
        setExcludedBrands(prev => prev.filter(b => b !== brand));
    };

    const countActiveFilters = () => {
        let count = 0;

        if (filters.marketplace.length > 0 && filters.marketplace.length < 3) count++;
        if (filters.search_text) count++;
        if (filters.min_cost > 0) count++;
        if (filters.max_cost > 0) count++;
        if (filters.min_sell_price > 0) count++;
        if (filters.max_sell_price > 0) count++;
        if (filters.min_roi >= 0) count++;
        if (filters.max_roi >= 0) count++;
        if (filters.min_profit >= 0) count++;
        if (filters.max_profit >= 0) count++;
        if (filters.profitable_only) count++;
        if (filters.coupon_amount > 0) count++;
        if (filters.days_since_found > 0) count++;

        if (filters.amazon.exclude_amazon_selling) count++;
        if (filters.amazon.only_amazon_selling) count++;
        if (filters.amazon.exclude_walmart_3p) count++;
        if (filters.amazon.no_variations) count++;
        if (filters.amazon.no_private_label) count++;
        if (filters.amazon.min_bsr > 0) count++;
        if (filters.amazon.max_bsr > 0) count++;
        if (filters.amazon.min_bsr_percent > 0) count++;
        if (filters.amazon.max_bsr_percent > 0) count++;
        if (filters.amazon.min_offers >= 0) count++;
        if (filters.amazon.max_offers >= 0) count++;
        if (filters.amazon.min_bsr_drops_30 > 0) count++;
        if (filters.amazon.min_bsr_drops_90 > 0) count++;
        if (filters.amazon.min_est_sales > 0) count++;
        if (filters.amazon.max_weight > 0) count++;
        if (filters.amazon.gated_status !== 'all') count++;
        if (filters.amazon.fulfillment !== 'fba') count++;

        if (filters.walmart.exclude_walmart_selling) count++;
        if (filters.walmart.only_walmart_selling) count++;
        if (filters.walmart.exclude_walmart_3p) count++;
        if (filters.walmart.fulfillment !== 'wfs') count++;
        if (filters.walmart.min_offers >= 0) count++;
        if (filters.walmart.max_offers >= 0) count++;
        if (filters.walmart.in_stock_only) count++;
        if (filters.walmart.min_reviews > 0) count++;
        if (filters.walmart.min_rating > 0) count++;
        if (filters.walmart.selected_badges.length > 0) count++;

        if (filters.ebay.min_offers >= 0) count++;
        if (filters.ebay.max_offers >= 0) count++;
        if (filters.ebay.min_est_sales > 0) count++;
        if (filters.ebay.exclude_walmart_3p) count++;

        if (filters.suppliers.selected_suppliers.length > 0) count++;
        if (bannedSuppliers.length > 0) count++;

        if (excludedBrands.length > 0) count++;
        if (filters.favorites_only) count++;
        if (filters.remove_multipacks) count++;

        if (filters.shared.selected_categories.length > 0) count++;
        if ((filters.shared.selected_brands || []).length > 0) count++;

        return count;
    };

    const InputField = ({ label, type = "number", value, onChange, placeholder, min, max, step = "1" }) => {
        const [localValue, setLocalValue] = useState(value);

        React.useEffect(() => {
            setLocalValue(value);
        }, [value]);

        return (
            <div className={(hasUnscannedDeals && label.includes("Cost ($)")) ? 'opacity-40 pointer-events-none' : ''}>
                {label && <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>}
                <input
                    type={type}
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onBlur={(e) => {
                        if (type === 'number') {
                            const val = e.target.value;
                            onChange(val === '' ? '' : parseFloat(val));
                        } else {
                            onChange(e.target.value);
                        }
                    }}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
            </div>
        );
    };

    const CheckboxField = ({ label, checked, onChange, tooltip }) => (
        <label className="flex items-center gap-1.5 cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-3.5 h-3.5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
            />
            <span className="text-xs text-gray-700">
                {label}
                {tooltip && <span className="text-gray-500 ml-1" title={tooltip}>ⓘ</span>}
            </span>
        </label>
    );

    const SelectField = ({ label, value, onChange, options }) => (
        <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        </div>
    );

    const Modal = ({ isOpen, onClose, title, children }) => {
        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto mt-10" onClick={(e) => e.stopPropagation()}>
                    <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                    <div className="p-6">
                        {children}
                    </div>
                    <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                        >
                            Apply
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-3">
            {/* Header with Active Filter Count, Reset, Save, Load, Export */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {countActiveFilters() > 0 && (
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                            {countActiveFilters()}
                        </span>
                    )}

                    <div className="flex items-center gap-2">
                        <label className="block text-xs font-bold text-gray-700 whitespace-nowrap">
                            {window.location.pathname.includes('storefront-scan') ? 'First Seen:' : 'Updated:'}
                        </label>
                        <select
                            value={filters.days_since_found ?? -1}
                            onChange={(e) => setFilters(prev => ({ ...prev, days_since_found: parseInt(e.target.value) }))}
                            className="py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        >
                            <option value={-1}>Any Time</option>
                            <option value={1}>Today</option>
                            <option value={3}>Last 3 Days</option>
                            <option value={7}>Last 7 Days</option>
                            <option value={14}>Last 14 Days</option>
                            <option value={30}>Last 30 Days</option>
                        </select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer ml-2 select-none">
                        <input
                            type="checkbox"
                            checked={autoPackDetection}
                            onChange={(e) => setAutoPackDetection(e.target.checked)}
                            className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                        />
                        <span className="text-xs text-gray-700 font-semibold flex items-center">
                            Multi Pack Detection
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer ml-2 select-none">
                        <input
                            type="checkbox"
                            checked={!!filters.remove_multipacks}
                            onChange={(e) => setFilters(prev => ({ ...prev, remove_multipacks: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                        />
                        <span className="text-xs text-gray-700 font-semibold flex items-center">
                            Remove Multi Packs
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer ml-4 select-none">
                        <input
                            type="checkbox"
                            checked={filters.favorites_only}
                            onChange={(e) => setFilters(prev => ({ ...prev, favorites_only: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                        />
                        <span className="text-xs font-semibold flex items-center gap-1 text-red-500 hover:text-red-600">
                            <Heart
                                size={18}
                                fill='currentColor'
                            /> Only
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer ml-4 select-none" title="Compact view = row for each item instead of a box">
                        <input
                            type="checkbox"
                            checked={isCompactView}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                setIsCompactView(newValue);
                                localStorage.setItem('compact_deal_view', newValue.toString());
                            }}
                            className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                        />
                        <span className="text-xs text-gray-700 font-semibold flex items-center gap-1">
                            Compact
                        </span>
                    </label>
                </div>
                <div className="flex items-center gap-2">
                    {/* Supplier Filter Button - ADD THIS */}
                    <button
                        onClick={openSupplierModal}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                        title="Filter Suppliers"
                    >
                        <span>🏪</span>
                        Filter Suppliers
                    </button>

                    {/* Export CSV Button */}
                    <button
                        onClick={openExportModal}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        title="Export filtered deals to CSV"
                    >
                        <Download size={14} />
                        Export Deals CSV
                    </button>

                    {/* Save Filter Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowSaveSlots(!showSaveSlots)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            <Save size={14} />
                            Save Filters
                        </button>
                        {showSaveSlots && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 p-2 min-w-[200px]">
                                <div className="text-xs font-semibold text-gray-700 mb-2 pb-2 border-b">Save to Slot</div>
                                {[1, 2, 3, 4, 5, 6].map(slot => {
                                    const slotInfo = getSlotInfo(slot);
                                    return (
                                        <button
                                            key={slot}
                                            onClick={() => saveFilterToSlot(slot)}
                                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 rounded transition-colors"
                                        >
                                            <div className="font-semibold">Slot {slot}</div>
                                            {slotInfo.exists && (
                                                <div className="text-[10px] text-gray-500 truncate">
                                                    {slotInfo.name}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Load Filter Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowLoadSlots(!showLoadSlots)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                        >
                            <FolderOpen size={14} />
                            Load Filters
                        </button>
                        {showLoadSlots && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 p-2 min-w-[200px]">
                                <div className="text-xs font-semibold text-gray-700 mb-2 pb-2 border-b">Load from Slot</div>
                                {[1, 2, 3, 4, 5, 6].map(slot => {
                                    const slotInfo = getSlotInfo(slot);
                                    return (
                                        <button
                                            key={slot}
                                            onClick={() => loadFilterFromSlot(slot)}
                                            disabled={!slotInfo.exists}
                                            className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors ${slotInfo.exists ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                                }`}
                                        >
                                            <div className="font-semibold">Slot {slot}</div>
                                            {slotInfo.exists ? (
                                                <div className="text-[10px] text-gray-500 truncate">
                                                    {slotInfo.name}
                                                </div>
                                            ) : (
                                                <div className="text-[10px] text-gray-400">Empty</div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Reset Button */}
                    <button
                        onClick={resetFilters}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <RotateCcw size={14} />
                        Reset Filters
                    </button>
                </div>
            </div>

            {/* Shared Filters */}
            <div ref={generalFiltersRef} className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {/* Marketplace Selection */}
                <div className="md:col-span-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marketplaces</label>
                    <div className="relative">
                        <div className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between gap-1 min-h-[32px]"
                            onClick={(e) => {
                                const dd = e.currentTarget.nextSibling;
                                dd.classList.toggle('hidden');
                            }}
                        >
                            <span className="text-gray-700 truncate">
                                {filters.marketplace.length === 0
                                    ? 'All Marketplaces'
                                    : filters.marketplace.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}
                            </span>
                            <svg className="w-3 h-3 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                        <div className="marketplace-dd hidden absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-50 p-2 space-y-1">
                            {[
                                { value: 'amazon', label: 'Amazon' },
                                { value: 'walmart', label: 'Walmart' },
                                { value: 'ebay', label: 'eBay' },
                            ].map(({ value, label }) => (
                                <label key={value} className="flex items-center gap-2 px-1 py-1 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.marketplace.includes(value)}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            marketplace: e.target.checked
                                                ? [...prev.marketplace, value]
                                                : prev.marketplace.filter(m => m !== value)
                                        }))}
                                        className="w-3.5 h-3.5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                                    />
                                    <span className="text-xs text-gray-700">{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="md:col-span-1">
                    <InputField
                        label="Search"
                        type="text"
                        value={filters.search_text}
                        onChange={(value) => setFilters(prev => ({ ...prev, search_text: value }))}
                        placeholder="Title, Brand, ASIN, UPC..."
                    />
                </div>

                {/* Coupon/Discount Percentage */}
                <InputField
                    label="Coupon/Discount (%)"
                    value={filters.coupon_amount > 0 ? filters.coupon_amount : ''}
                    onChange={(value) => setFilters(prev => ({ ...prev, coupon_amount: value }))}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.1"
                />

                {/* Cost Price Range */}
                <InputField
                    label="Min Cost ($)"
                    value={filters.min_cost > 0 ? filters.min_cost : ''}
                    onChange={(value) => setFilters(prev => ({ ...prev, min_cost: value }))}
                    placeholder="Any"
                    min="0"
                    step="0.01"
                />
                <InputField
                    label="Max Cost ($)"
                    value={filters.max_cost > 0 ? filters.max_cost : ''}
                    onChange={(value) => setFilters(prev => ({ ...prev, max_cost: value }))}
                    placeholder="Any"
                    min="0"
                    step="0.01"
                />

                {/* Sell Price Range */}
                <InputField
                    label="Min Sell ($)"
                    value={filters.min_sell_price > 0 ? filters.min_sell_price : ''}
                    onChange={(value) => setFilters(prev => ({ ...prev, min_sell_price: value }))}
                    placeholder="Any"
                    min="0"
                    step="0.01"
                />
                <InputField
                    label="Max Sell ($)"
                    value={filters.max_sell_price > 0 ? filters.max_sell_price : ''}
                    onChange={(value) => setFilters(prev => ({ ...prev, max_sell_price: value }))}
                    placeholder="Any"
                    min="0"
                    step="0.01"
                />

                {/* ROI Range */}
                <div className={hasUnscannedDeals ? 'opacity-40 pointer-events-none' : ''}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">ROI (%)</label>
                    <div className="flex items-center gap-1">
                        <InputField
                            label=""
                            value={filters.min_roi >= 0 ? filters.min_roi : ''}
                            onChange={(value) => {
                                if (value === '') setFilters(prev => ({ ...prev, min_roi: -1 }));
                                else if (!isNaN(parseFloat(value))) setFilters(prev => ({ ...prev, min_roi: parseFloat(value) }));
                            }}
                            placeholder="Min"
                            min="0"
                            step="1"
                        />
                        <span className="text-gray-400 text-xs">–</span>
                        <InputField
                            label=""
                            value={filters.max_roi >= 0 ? filters.max_roi : ''}
                            onChange={(value) => {
                                if (value === '') setFilters(prev => ({ ...prev, max_roi: -1 }));
                                else if (!isNaN(parseFloat(value))) setFilters(prev => ({ ...prev, max_roi: parseFloat(value) }));
                            }}
                            placeholder="Max"
                            min="0"
                            step="1"
                        />
                    </div>
                </div>

                {/* Profit Range */}
                <div className={hasUnscannedDeals ? 'opacity-40 pointer-events-none' : ''}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Profit ($)</label>
                    <div className="flex items-center gap-1">
                        <InputField
                            label=""
                            value={filters.min_profit >= 0 ? filters.min_profit : ''}
                            onChange={(value) => {
                                if (value === '') setFilters(prev => ({ ...prev, min_profit: -1 }));
                                else if (!isNaN(parseFloat(value))) setFilters(prev => ({ ...prev, min_profit: parseFloat(value) }));
                            }}
                            placeholder="Min"
                            min="0"
                            step="0.01"
                        />
                        <span className="text-gray-400 text-xs">–</span>
                        <InputField
                            label=""
                            value={filters.max_profit >= 0 ? filters.max_profit : ''}
                            onChange={(value) => {
                                if (value === '') setFilters(prev => ({ ...prev, max_profit: -1 }));
                                else if (!isNaN(parseFloat(value))) setFilters(prev => ({ ...prev, max_profit: parseFloat(value) }));
                            }}
                            placeholder="Max"
                            min="0"
                            step="0.01"
                        />
                    </div>
                </div>

                {/* Marketplace Filter Buttons - Last Column */}
                <div ref={marketplaceButtonsRef} className="md:col-span-1 flex flex-col gap-1">
                    <label className="block text-xs font-medium text-gray-700">Marketplace Related Filters</label>
                    <div className="grid gap-1" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr' }}>
                        <button
                            onClick={() => setShowAmazonModal(true)}
                            className="px-2 py-1 bg-transparent border-2 border-orange-500 rounded-lg hover:bg-orange-500 hover:bg-opacity-10 transition-colors flex items-center justify-center"
                            title="Amazon Filters"
                        >
                            <img src={MARKETPLACE_LOGOS.amazon} alt="Amazon" className="w-full h-full object-contain max-w-[28px] max-h-[28px] min-w-[14px] min-h-[14px]" />
                        </button>
                        <button
                            onClick={openWalmartModal}
                            className="px-2 py-1 bg-transparent border-2 border-blue-500 rounded-lg hover:bg-blue-500 hover:bg-opacity-10 transition-colors flex items-center justify-center"
                            title="Walmart Filters"
                        >
                            <img src={MARKETPLACE_LOGOS.walmart} alt="Walmart" className="w-full h-full object-contain max-w-[28px] max-h-[28px] min-w-[14px] min-h-[14px]" />
                        </button>
                        <button
                            onClick={() => setShowEbayModal(true)}
                            className="px-2 py-1 bg-transparent border-2 border-red-500 rounded-lg hover:bg-red-500 hover:bg-opacity-10 transition-colors flex items-center justify-center"
                            title="eBay Filters"
                        >
                            <img src={MARKETPLACE_LOGOS.ebay} alt="eBay" className="w-full h-full object-contain max-w-[28px] max-h-[28px] min-w-[14px] min-h-[14px]" />
                        </button>
                        <button
                            onClick={openCategoryModal}
                            className="px-1 py-1 bg-transparent border-2 border-green-500 rounded-lg hover:bg-green-500 hover:bg-opacity-10 transition-colors flex items-center justify-center text-xs font-semibold text-green-700"
                            title="Category Filter"
                        >
                            Category
                        </button>
                        <button
                            onClick={openBrandModal}
                            className="px-1 py-1 bg-transparent border-2 border-purple-500 rounded-lg hover:bg-purple-500 hover:bg-opacity-10 transition-colors flex items-center justify-center text-xs font-semibold text-purple-700"
                            title="Brand Exclusion"
                        >
                            Brand
                        </button>
                    </div>
                </div>
            </div>

            {/* Amazon Modal */}
            <Modal isOpen={showAmazonModal} onClose={() => setShowAmazonModal(false)} title="Amazon Filters">
                <div className="space-y-4">
                    {/* Checkboxes */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <CheckboxField
                            label="Exclude Amazon"
                            checked={filters.amazon.exclude_amazon_selling}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, exclude_amazon_selling: checked }
                            }))}
                            tooltip="Filter out products where Amazon is a seller"
                        />
                        <CheckboxField
                            label="Only Amazon Seller"
                            checked={filters.amazon.only_amazon_selling}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, only_amazon_selling: checked }
                            }))}
                            tooltip="Show only products where Amazon is the seller"
                        />
                        <CheckboxField
                            label="Exclude Walmart 3P"
                            checked={filters.amazon.exclude_walmart_3p}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, exclude_walmart_3p: checked }
                            }))}
                            tooltip="If supplier is Walmart and seller is not Walmart, filter it out"
                        />
                        <CheckboxField
                            label="No Variations"
                            checked={filters.amazon.no_variations}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, no_variations: checked }
                            }))}
                        />
                        <CheckboxField
                            label="No Private Label"
                            checked={filters.amazon.no_private_label}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, no_private_label: checked }
                            }))}
                            tooltip="Less than 4 offers"
                        />
                    </div>

                    {/* BSR Filters */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <InputField
                            label="Min BSR"
                            value={filters.amazon.min_bsr > 0 ? filters.amazon.min_bsr : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, min_bsr: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Max BSR"
                            value={filters.amazon.max_bsr > 0 ? filters.amazon.max_bsr : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, max_bsr: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />

                        <InputField
                            label="Min BSR %"
                            value={filters.amazon.min_bsr_percent > 0 ? filters.amazon.min_bsr_percent : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, min_bsr_percent: value }
                            }))}
                            placeholder="Any"
                            min="0"
                            max="100"
                            step="0.1"
                        />
                        <InputField
                            label="Max BSR %"
                            value={filters.amazon.max_bsr_percent > 0 ? filters.amazon.max_bsr_percent : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, max_bsr_percent: value }
                            }))}
                            placeholder="Any"
                            min="0"
                            max="100"
                            step="0.1"
                        />
                    </div>

                    {/* Offers, BSR Drops, Sales */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <InputField
                            label="Min Offers"
                            value={filters.amazon.min_offers >= 0 ? filters.amazon.min_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        amazon: { ...prev.amazon, min_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        amazon: { ...prev.amazon, min_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Max Offers"
                            value={filters.amazon.max_offers >= 0 ? filters.amazon.max_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        amazon: { ...prev.amazon, max_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        amazon: { ...prev.amazon, max_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="BSR Drops 30d"
                            value={filters.amazon.min_bsr_drops_30 > 0 ? filters.amazon.min_bsr_drops_30 : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, min_bsr_drops_30: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="BSR Drops 90d"
                            value={filters.amazon.min_bsr_drops_90 > 0 ? filters.amazon.min_bsr_drops_90 : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, min_bsr_drops_90: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Min Sales/mo"
                            value={filters.amazon.min_est_sales > 0 ? filters.amazon.min_est_sales : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, min_est_sales: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Max Weight (lbs)"
                            value={filters.amazon.max_weight > 0 ? filters.amazon.max_weight : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, max_weight: value }
                            }))}
                            placeholder="Any"
                            min="0"
                            step="0.1"
                        />
                    </div>

                    {/* Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <SelectField
                            label="Gated Status"
                            value={filters.amazon.gated_status}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, gated_status: value }
                            }))}
                            options={[
                                { value: 'all', label: 'Show All' },
                                { value: 'ungated', label: 'Show Only Ungated' },
                                { value: 'not_gated', label: 'Do Not Show Gated' },
                                { value: 'checking', label: 'Show Checking' }
                            ]}
                        />
                        <SelectField
                            label="Fulfillment"
                            value={filters.amazon.fulfillment}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                amazon: { ...prev.amazon, fulfillment: value }
                            }))}
                            options={[
                                { value: 'fba', label: 'FBA (include FBA fees)' },
                                { value: 'fbm', label: 'FBM (no FBA fees)' }
                            ]}
                        />
                    </div>
                </div>
            </Modal>

            {/* Walmart Modal */}
            <Modal isOpen={showWalmartModal} onClose={() => setShowWalmartModal(false)} title="Walmart Filters">
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <CheckboxField
                            label="Exclude Walmart"
                            checked={filters.walmart.exclude_walmart_selling}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, exclude_walmart_selling: checked }
                            }))}
                            tooltip="Filter out products where Walmart is a seller"
                        />
                        <CheckboxField
                            label="Only Walmart Sells"
                            checked={filters.walmart.only_walmart_selling}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, only_walmart_selling: checked }
                            }))}
                            tooltip="Show only products where Walmart is the seller"
                        />
                        <CheckboxField
                            label="Exclude Walmart 3P"
                            checked={filters.walmart.exclude_walmart_3p}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, exclude_walmart_3p: checked }
                            }))}
                            tooltip="If supplier is Walmart and seller is not Walmart, filter it out"
                        />
                        <CheckboxField
                            label="In Stock Only"
                            checked={filters.walmart.in_stock_only}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, in_stock_only: checked }
                            }))}
                        />
                        <CheckboxField
                            label="Seller In Stock Only (Storefront Scan)"
                            checked={filters.walmart.seller_in_stock_only}
                            onChange={(checked) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, seller_in_stock_only: checked }
                            }))}
                        />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <InputField
                            label="Min Offers"
                            value={filters.walmart.min_offers >= 0 ? filters.walmart.min_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        walmart: { ...prev.walmart, min_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        walmart: { ...prev.walmart, min_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Max Offers"
                            value={filters.walmart.max_offers >= 0 ? filters.walmart.max_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        walmart: { ...prev.walmart, max_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        walmart: { ...prev.walmart, max_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Min Reviews"
                            value={filters.walmart.min_reviews > 0 ? filters.walmart.min_reviews : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, min_reviews: value }
                            }))}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Min Rating"
                            value={filters.walmart.min_rating > 0 ? filters.walmart.min_rating : ''}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                walmart: { ...prev.walmart, min_rating: value }
                            }))}
                            placeholder="Any"
                            min="0"
                            max="5"
                            step="0.1"
                        />
                    </div>

                    <SelectField
                        label="Fulfillment"
                        value={filters.walmart.fulfillment}
                        onChange={(value) => setFilters(prev => ({
                            ...prev,
                            walmart: { ...prev.walmart, fulfillment: value }
                        }))}
                        options={[
                            { value: 'wfs', label: 'WFS (include WFS fees)' },
                            { value: 'seller_fulfilled', label: 'Seller Fulfilled (no WFS fees)' }
                        ]}
                    />

                    {/* Badge Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Filter by Badges (select badges to show)
                        </label>
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                            {walmartBadges.length > 0 ? (
                                walmartBadges.map((badgeText, index) => (
                                    <label key={index} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                        <input
                                            type="checkbox"
                                            checked={filters.walmart.selected_badges.includes(badgeText)}
                                            onChange={(e) => {
                                                const newBadges = e.target.checked
                                                    ? [...filters.walmart.selected_badges, badgeText]
                                                    : filters.walmart.selected_badges.filter(b => b !== badgeText);

                                                setFilters(prev => ({
                                                    ...prev,
                                                    walmart: { ...prev.walmart, selected_badges: newBadges }
                                                }));
                                            }}
                                            className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                                        />
                                        <span className="text-sm text-gray-700">{badgeText}</span>
                                    </label>
                                ))
                            ) : (
                                <div className="text-sm text-gray-500 text-center py-2">
                                    No badges found in current deals
                                </div>
                            )}
                        </div>

                        {/* Quick Actions */}
                        {walmartBadges.length > 0 && (
                            <div className="flex gap-2 mt-2">
                                <button
                                    onClick={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            walmart: { ...prev.walmart, selected_badges: walmartBadges }
                                        }));
                                    }}
                                    className="text-xs px-2 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            walmart: { ...prev.walmart, selected_badges: [] }
                                        }));
                                    }}
                                    className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                >
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* eBay Modal */}
            <Modal isOpen={showEbayModal} onClose={() => setShowEbayModal(false)} title="eBay Filters">
                <div className="space-y-4">
                    <CheckboxField
                        label="Exclude Walmart 3P"
                        checked={filters.ebay.exclude_walmart_3p}
                        onChange={(checked) => setFilters(prev => ({
                            ...prev,
                            ebay: { ...prev.ebay, exclude_walmart_3p: checked }
                        }))}
                        tooltip="If supplier is Walmart and seller is not Walmart, filter it out"
                    />
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
                        <InputField
                            label="Min Offers"
                            value={filters.ebay.min_offers >= 0 ? filters.ebay.min_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        ebay: { ...prev.ebay, min_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        ebay: { ...prev.ebay, min_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                        <InputField
                            label="Max Offers"
                            value={filters.ebay.max_offers >= 0 ? filters.ebay.max_offers : ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setFilters(prev => ({
                                        ...prev,
                                        ebay: { ...prev.ebay, max_offers: -1 }
                                    }))
                                } else if (!isNaN(parseFloat(value))) {
                                    setFilters(prev => ({
                                        ...prev,
                                        ebay: { ...prev.ebay, max_offers: parseFloat(value) }
                                    }))
                                }
                            }}
                            placeholder="Any"
                            min="0"
                        />
                    </div>
                </div>
            </Modal>

            {/* Brand Exclusion Modal */}
            <Modal isOpen={showBrandModal} onClose={() => setShowBrandModal(false)} title="Brand Filters">
                <div className="mb-4">
                    <h4 className="text-base font-bold text-teal-700 mb-1">✅ Only Show Brands</h4>
                    <p className="text-xs text-gray-500 mb-3">Select brands to show ONLY those. Applies to all marketplaces.</p>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Show Only These Brands ({(filters.shared.selected_brands || []).length} selected)
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setFilters(prev => ({
                                    ...prev,
                                    shared: { ...prev.shared, selected_brands: allBrands }
                                }))}
                                className="text-xs px-2 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => setFilters(prev => ({
                                    ...prev,
                                    shared: { ...prev.shared, selected_brands: [] }
                                }))}
                                className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                        {allBrands.length > 0 ? (
                            allBrands.map((brand) => (
                                <label key={brand} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                    <input
                                        type="checkbox"
                                        checked={(filters.shared.selected_brands || []).includes(brand)}
                                        onChange={(e) => setFilters(prev => {
                                            const current = prev.shared.selected_brands || [];
                                            return {
                                                ...prev,
                                                shared: {
                                                    ...prev.shared,
                                                    selected_brands: e.target.checked
                                                        ? [...current, brand]
                                                        : current.filter(b => b !== brand)
                                                }
                                            };
                                        })}
                                        className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{brand}</span>
                                </label>
                            ))
                        ) : (
                            <div className="text-sm text-gray-500 text-center py-2">
                                No brands found in current deals
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Leave empty to show all brands.</p>
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="text-base font-bold text-red-700 mb-1">🚫 Exclude Brands</h4>
                        <p className="text-xs text-gray-500">Brands listed here are NEVER shown (Amazon & Walmart).</p>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <Upload size={16} />
                            Import CSV
                        </button>
                        <button
                            onClick={exportBrandsToCSV}
                            disabled={excludedBrands.length === 0}
                            className="flex items-center gap-1 px-3 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.txt"
                            onChange={importBrandsFromCSV}
                            className="hidden"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add Brands (comma or line separated)
                        </label>
                        <textarea
                            id="brand-input"
                            placeholder="Enter brands to exclude..."
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            rows="3"
                        />
                        <button
                            onClick={() => {
                                const input = document.getElementById('brand-input');
                                addBrand(input.value);
                                input.value = '';
                            }}
                            className="mt-2 px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                        >
                            Add Brands
                        </button>
                    </div>

                    {excludedBrands.length > 0 && (
                        <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                Excluded Brands ({excludedBrands.length})
                            </div>
                            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                                {excludedBrands.map((brand, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm group hover:bg-gray-100 transition-colors"
                                    >
                                        <span className="text-gray-700">{brand}</span>
                                        <button
                                            onClick={() => removeBrand(brand)}
                                            className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Supplier Filter Modal */}
            <Modal isOpen={showSupplierModal} onClose={() => setShowSupplierModal(false)} title="Supplier Filters">
                <div className="space-y-4">
                    {/* Tab-like sections */}
                    <div className="border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-600">
                            <div className="rounded text-xs mb-4">
                                <strong>Whitelist:</strong> Select specific suppliers to show ONLY those&emsp;<span className='text-gray-100'>|</span>&emsp;
                                <strong>Blacklist:</strong> Ban suppliers to NEVER show them again
                            </div>
                        </div>
                    </div>

                    {/* Supplier Selection (Whitelist) */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Select Suppliers to Show ({filters.suppliers.selected_suppliers.length} selected)
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={selectAllSuppliers}
                                    className="text-xs px-2 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={clearSupplierSelection}
                                    className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                            {allSuppliers.filter(s => !bannedSuppliers.includes(s.name)).length > 0 ? (
                                allSuppliers
                                    .filter(s => !bannedSuppliers.includes(s.name))
                                    .map((supplier, index) => (
                                        <div key={index} className="flex items-center justify-between hover:bg-gray-50 p-2 rounded group">
                                            <label className="flex items-center gap-2 cursor-pointer flex-1">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.suppliers.selected_suppliers.includes(supplier.name)}
                                                    onChange={() => toggleSupplierSelection(supplier.name)}
                                                    className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                                                />
                                                {supplier.logo && (
                                                    <img
                                                        src={supplier.logo}
                                                        alt={supplier.name}
                                                        className="w-6 h-6 object-contain"
                                                    />
                                                )}
                                                <span className="text-sm text-gray-700">{supplier.name}</span>
                                            </label>
                                            <button
                                                onClick={() => banSupplier(supplier.name)}
                                                className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                                title="Ban this supplier permanently"
                                            >
                                                Ban
                                            </button>
                                        </div>
                                    ))
                            ) : (
                                <div className="text-sm text-gray-500 text-center py-2">
                                    All suppliers are banned
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Banned Suppliers (Blacklist) */}
                    {bannedSuppliers.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-red-700">
                                    Banned Suppliers ({bannedSuppliers.length})
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => supplierFileInputRef.current?.click()}
                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                    >
                                        <Upload size={14} />
                                        Import
                                    </button>
                                    <button
                                        onClick={exportBannedSuppliersToCSV}
                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                    >
                                        <Download size={14} />
                                        Export
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-48 overflow-y-auto border border-red-200 bg-red-50 rounded-lg p-2 space-y-1">
                                {bannedSuppliers.map((supplierName, index) => {
                                    const supplierInfo = allSuppliers.find(s => s.name === supplierName);
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between bg-white px-3 py-2 rounded text-sm group hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-2">
                                                {supplierInfo?.logo && (
                                                    <img
                                                        src={supplierInfo.logo}
                                                        alt={supplierName}
                                                        className="w-5 h-5 object-contain"
                                                    />
                                                )}
                                                <span className="text-gray-700">{supplierName}</span>
                                            </div>
                                            <button
                                                onClick={() => unbanSupplier(supplierName)}
                                                className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                Unban
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <input
                                ref={supplierFileInputRef}
                                type="file"
                                accept=".csv,.txt"
                                onChange={importBannedSuppliersFromCSV}
                                className="hidden"
                            />
                        </div>
                    )}
                </div>
            </Modal>

            {/* Category Filter Modal */}
            <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title="Category Filter">
                <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Filter by Categories ({filters.shared.selected_categories.length} selected)
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setFilters(prev => ({
                                        ...prev,
                                        shared: { ...prev.shared, selected_categories: allCategories }
                                    }));
                                }}
                                className="text-xs px-2 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => {
                                    setFilters(prev => ({
                                        ...prev,
                                        shared: { ...prev.shared, selected_categories: [] }
                                    }));
                                }}
                                className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                        {allCategories.length > 0 ? (
                            allCategories.map((category, index) => (
                                <label key={index} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                    <input
                                        type="checkbox"
                                        checked={filters.shared.selected_categories.includes(category)}
                                        onChange={(e) => {
                                            const newCategories = e.target.checked
                                                ? [...filters.shared.selected_categories, category]
                                                : filters.shared.selected_categories.filter(c => c !== category);

                                            setFilters(prev => ({
                                                ...prev,
                                                shared: { ...prev.shared, selected_categories: newCategories }
                                            }));
                                        }}
                                        className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{category}</span>
                                </label>
                            ))
                        ) : (
                            <div className="text-sm text-gray-500 text-center py-2">
                                No categories found in current deals
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} title="Export Filtered Deals">
                <div className="space-y-4">
                    <div className="flex flex-col items-center gap-3">
                        <p className="text-sm text-gray-600 text-center">
                            This can also be used with <span className="font-medium">Ungate Machine, Repricer, and more</span>.
                            If you need any help, ask in the <span className="font-medium">communities</span>, join our{' '}
                            <span className="font-medium">weekly webinars</span>, or{' '}
                            <span className="font-medium">contact us</span>.
                        </p>
                        <button
                            onClick={handleExport}
                            className="px-4 py-2 text-m font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Click here to Export CSV
                        </button>
                    </div>
                </div>
            </Modal>

            <style>{`
            @keyframes tutorialPulse {
                0%, 100% { box-shadow: 0 0 0 3px rgba(45,212,191,0.6), 0 0 16px rgba(45,212,191,0.4); }
                50% { box-shadow: 0 0 0 6px rgba(45,212,191,0.2), 0 0 32px rgba(45,212,191,0.6); }
            }
            .tutorial-highlight {
                outline: 2px solid #2dd4bf;
                outline-offset: 4px;
                border-radius: 8px;
                animation: tutorialPulse 1.5s ease-in-out infinite;
            }
        `}</style>

            {tutorialStep !== null && (() => {
                const steps = [
                    {
                        ref: generalFiltersRef,
                        title: '1️⃣ General Filters',
                        description: 'Filter by price, ROI, profit, cost, search, coupon - applies to all deals across every marketplace.',
                    },
                    {
                        ref: marketplaceButtonsRef,
                        title: '2️⃣ Marketplace Related Filters',
                        description: 'Click Amazon, Walmart, or eBay for deep filters: BSR, BSR%, drops, gated status, # sellers, est. sales, stock, fulfillment, weight, ratings, reviews, badges & more.',
                    },
                ];
                const step = steps[tutorialStep];
                const isLast = tutorialStep === steps.length - 1;

                // Apply/remove highlight class
                steps.forEach((s, i) => {
                    if (s.ref?.current) {
                        if (i === tutorialStep) s.ref.current.classList.add('tutorial-highlight');
                        else s.ref.current.classList.remove('tutorial-highlight');
                    }
                });

                const el = step.ref?.current;
                const rect = el?.getBoundingClientRect();

                return (
                    <div
                        style={{
                            position: 'absolute',
                            width: 360,
                            background: 'white',
                            borderRadius: 12,
                            padding: '14px 16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            border: '1px solid #e5e7eb',
                            zIndex: 1000,
                        }}
                    >
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{step.title}</div>
                        <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, marginBottom: 12 }}>{step.description}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                                {steps.map((_, i) => (
                                    <div key={i} style={{ width: i === tutorialStep ? 18 : 6, height: 6, borderRadius: 3, background: i === tutorialStep ? '#0d9488' : '#d1d5db', transition: 'all 0.3s' }} />
                                ))}
                            </div>
                            <button onClick={dismissTutorial} style={{ fontSize: 12, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>Skip</button>
                            <button
                                onClick={isLast ? dismissTutorial : () => setTutorialStep(t => t + 1)}
                                style={{ fontSize: 13, fontWeight: 600, color: 'white', background: '#0d9488', border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer' }}
                            >
                                {isLast ? 'Got it!' : 'Next →'}
                            </button>
                        </div>
                    </div>
                );
            })()}

        </div>
    );
};

export default React.memo(AdvancedFilters, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters) &&
        prevProps.isCompactView === nextProps.isCompactView &&
        prevProps.afRender === nextProps.afRender &&
        prevProps.hasUnscannedDeals === nextProps.hasUnscannedDeals &&
        prevProps.autoPackDetection === nextProps.autoPackDetection;
});